import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { fmtRON, imgUrl, ProductCard } from "../components/ProductCard";
import { toast } from "sonner";
import { Zap, ShoppingCart, Minus, Plus, ArrowLeft, Truck, ShieldCheck, Wrench, Star, Send } from "lucide-react";

const SPEC_LABELS = {
  motor: "Motor", baterie: "Baterie", viteza: "Viteza maxima", autonomie: "Autonomie",
  greutate: "Greutate", incarcare_max: "Incarcare maxima", roti: "Roti", frane: "Frane",
  ip: "Rezistenta la apa", timp_incarcare: "Timp incarcare", urcare: "Capacitate urcare",
  suspensie: "Suspensie", iluminare: "Iluminare LED", aplicatie: "Aplicatie mobila",
  dimensiuni: "Dimensiuni", cadru: "Cadru",
};

const Stars = ({ value, size = "w-4 h-4" }) => (
  <span className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= Math.round(value) ? "text-[#00E5FF]" : "text-neutral-700"}`}
        fill={i <= Math.round(value) ? "currentColor" : "none"} />
    ))}
  </span>
);

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [related, setRelated] = useState([]);
  const [rv, setRv] = useState({ name: "", rating: 5, comment: "" });
  const [rvLoading, setRvLoading] = useState(false);

  useEffect(() => {
    api.get(`/shop/products/${id}`).then((r) => { setP(r.data); document.title = `${r.data.name} — ${new Intl.NumberFormat("ro-RO").format(r.data.price)} RON | Troti-X`; }).catch(() => setErr(true));
    api.get(`/shop/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/shop/products").then((r) => setRelated(r.data.filter((x) => x.id !== id).slice(0, 4))).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rv.name.trim()) { toast.error("Spune-ne numele tau"); return; }
    setRvLoading(true);
    try {
      await api.post(`/shop/products/${id}/reviews`, rv);
      toast.success("Multumim pentru recenzie!");
      setRv({ name: "", rating: 5, comment: "" });
      const [r1, r2] = await Promise.all([api.get(`/shop/products/${id}/reviews`), api.get(`/shop/products/${id}`)]);
      setReviews(r1.data); setP(r2.data);
    } catch { toast.error("Eroare la trimitere"); }
    finally { setRvLoading(false); }
  };

  if (err) return <div className="max-w-4xl mx-auto px-6 py-32 text-center text-neutral-500">Produsul nu a fost gasit. <Link to="/magazin" className="text-[#00E5FF]">Inapoi la magazin</Link></div>;
  if (!p) return <div className="py-32 text-center text-neutral-600">Se incarca...</div>;

  const images = p.images || [];
  const main = imgUrl(images[imgIdx]?.path);
  const out = p.stock <= 0;
  const specs = Object.entries(p.specs || {}).filter(([, v]) => v);

  return (
    <div data-testid="product-page" className="max-w-7xl mx-auto px-6 py-12">
      <Link to="/magazin" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 hover:text-[#00E5FF] transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Inapoi la magazin
      </Link>
      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square bg-[#0B0B0B] border border-[#00E5FF]/20 overflow-hidden">
            {main ? <img src={main} alt={p.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-[#00E5FF]/20"><Zap className="w-24 h-24" /></div>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {images.map((im, i) => (
                <button key={im.id} data-testid={`gallery-thumb-${i}`} onClick={() => setImgIdx(i)}
                  className={`w-20 h-20 border overflow-hidden ${i === imgIdx ? "border-[#00E5FF]" : "border-neutral-800"}`}>
                  <img src={imgUrl(im.path)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl uppercase leading-tight">{p.name}</h1>
          {p.rating_count > 0 && (
            <div data-testid="product-rating" className="flex items-center gap-2 mt-2">
              <Stars value={p.rating_avg} />
              <span className="text-sm text-neutral-400">{p.rating_avg} · {p.rating_count} {p.rating_count === 1 ? "recenzie" : "recenzii"}</span>
            </div>
          )}
          {p.short_desc && <p className="text-neutral-400 text-sm mt-3">{p.short_desc}</p>}
          <div className="flex items-end gap-4 mt-6">
            <div className="text-[#00E5FF] font-bold text-4xl">{fmtRON(p.price)}</div>
            {p.old_price > 0 && p.old_price > p.price && <div className="text-neutral-500 line-through text-lg mb-1">{fmtRON(p.old_price)}</div>}
          </div>
          <div className={`text-xs uppercase tracking-widest mt-2 ${out ? "text-red-400" : "text-green-400"}`}>
            {out ? "Stoc epuizat" : `In stoc: ${p.stock} buc`}
          </div>

          {(p.compatible_models || []).length > 0 && (
            <div data-testid="compat-section" className="mt-6 border border-[#00E5FF]/20 bg-[#0B0B0B] p-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-2.5">// Compatibil cu</div>
              <div className="flex flex-wrap gap-2">
                {p.compatible_models.map((m) => (
                  <span key={m} data-testid={`compat-badge-${m}`} className="px-2.5 py-1 text-xs border border-[#00E5FF]/30 bg-[#00E5FF]/5 text-neutral-200">{m}</span>
                ))}
              </div>
              <div className="text-[10px] text-neutral-500 mt-2.5">Verifica modelul trotinetei tale inainte de comanda. Ai intrebari? Contacteaza-ne!</div>
            </div>
          )}

          {!out && (
            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center border border-[#00E5FF]/30">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-[#00E5FF]"><Minus className="w-4 h-4" /></button>
                <div data-testid="qty-value" className="w-10 text-center font-bold">{qty}</div>
                <button data-testid="qty-plus" onClick={() => setQty(Math.min(p.stock, qty + 1))} className="p-3 hover:text-[#00E5FF]"><Plus className="w-4 h-4" /></button>
              </div>
              <button data-testid="product-add-to-cart"
                onClick={() => { add(p, qty); toast.success(`${p.name} adaugat in cos`); }}
                className="flex-1 inline-flex items-center justify-center gap-3 bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-bold px-6 py-3.5 uppercase tracking-wider text-sm transition-colors">
                <ShoppingCart className="w-4 h-4" /> Adauga in cos
              </button>
            </div>
          )}
          {!out && (
            <button data-testid="product-buy-now"
              onClick={() => { add(p, qty); navigate("/cos"); }}
              className="w-full mt-3 border border-[#00E5FF]/40 hover:border-[#00E5FF] text-white font-semibold px-6 py-3.5 uppercase tracking-wider text-sm transition-colors">
              Cumpara acum
            </button>
          )}

          <div className="grid grid-cols-3 gap-3 mt-8 text-center">
            {[{ icon: Truck, t: "Livrare gratuita" }, { icon: ShieldCheck, t: "Garantie 24 luni" }, { icon: Wrench, t: "Service propriu" }].map((b, i) => (
              <div key={i} className="border border-[#00E5FF]/15 p-3">
                <b.icon className="w-5 h-5 text-[#00E5FF] mx-auto mb-1.5" />
                <div className="text-[10px] uppercase tracking-wider text-neutral-400">{b.t}</div>
              </div>
            ))}
          </div>

          {specs.length > 0 && (
            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-3">// Specificatii</div>
              <div className="divide-y divide-neutral-900 border border-neutral-900">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex justify-between px-4 py-3 text-sm">
                    <span className="text-neutral-500">{SPEC_LABELS[k] || k}</span>
                    <span className="font-medium text-right">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {p.description && (
            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-3">// Descriere</div>
              <p className="text-sm text-neutral-400 leading-relaxed whitespace-pre-wrap">{p.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* RECENZII */}
      <div className="mt-16 max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-4">// Recenzii clienti</div>
        {p.rating_count > 0 && (
          <div className="flex items-center gap-3 mb-6">
            <span className="font-display text-4xl">{p.rating_avg}</span>
            <div>
              <Stars value={p.rating_avg} size="w-5 h-5" />
              <div className="text-xs text-neutral-500 mt-1">{p.rating_count} {p.rating_count === 1 ? "recenzie" : "recenzii"}</div>
            </div>
          </div>
        )}
        <form data-testid="review-form" onSubmit={submitReview} className="border border-[#00E5FF]/20 bg-[#0B0B0B] p-5 mb-8">
          <div className="text-sm font-bold uppercase tracking-wider mb-4">Lasa o recenzie</div>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <input data-testid="review-name-input" value={rv.name} onChange={(e) => setRv({ ...rv, name: e.target.value })}
              placeholder="Numele tau" className="bg-black border border-[#00E5FF]/25 focus:border-[#00E5FF] px-4 py-2.5 text-sm flex-1 min-w-[180px]" />
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" data-testid={`review-star-${i}`} onClick={() => setRv({ ...rv, rating: i })} className="p-0.5">
                  <Star className={`w-7 h-7 transition-colors ${i <= rv.rating ? "text-[#00E5FF]" : "text-neutral-700 hover:text-neutral-500"}`}
                    fill={i <= rv.rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
          </div>
          <textarea data-testid="review-comment-input" value={rv.comment} onChange={(e) => setRv({ ...rv, comment: e.target.value })}
            rows={3} placeholder="Cum ti se pare trotineta? (optional)"
            className="w-full bg-black border border-[#00E5FF]/25 focus:border-[#00E5FF] px-4 py-2.5 text-sm" />
          <button data-testid="review-submit-btn" type="submit" disabled={rvLoading}
            className="mt-3 inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#5CEEFF] disabled:opacity-50 text-black font-bold px-6 py-2.5 uppercase tracking-wider text-xs transition-colors">
            <Send className="w-3.5 h-3.5" /> Trimite recenzia
          </button>
        </form>
        <div className="space-y-4">
          {reviews.length === 0 && <div className="text-sm text-neutral-500">Fii primul care lasa o recenzie!</div>}
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="border-b border-neutral-900 pb-4">
              <div className="flex items-center gap-3">
                <span className="font-bold text-sm">{r.name}</span>
                <Stars value={r.rating} size="w-3.5 h-3.5" />
                <span className="text-[10px] text-neutral-600 ml-auto">{new Date(r.created_at).toLocaleDateString("ro-RO")}</span>
              </div>
              {r.comment && <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* TROTINETE SIMILARE */}
      {related.length > 0 && (
        <div data-testid="related-products" className="mt-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-2">// Iti mai recomandam</div>
          <h2 className="font-display text-lg uppercase mb-8">Trotinete similare</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {related.map((rp) => <ProductCard key={rp.id} product={rp} />)}
          </div>
        </div>
      )}
    </div>
  );
}
